class MegaMenuColumns extends HTMLElement {
	connectedCallback() {
		this.trigger = this.querySelector("[data-mega-menu-trigger]");
		this.panel = this.querySelector("[data-mega-menu-panel]");
		this.item = this.closest(".list-menu-item");
		this.closeDelayMs = 280;
		this.closeTimeout = null;

		if (!this.trigger || !this.panel) return;

		this.onTriggerClick = this.onTriggerClick.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onPointerEnter = this.onPointerEnter.bind(this);
		this.onPointerLeave = this.onPointerLeave.bind(this);
		this.onFocusIn = this.onFocusIn.bind(this);
		this.onFocusOut = this.onFocusOut.bind(this);
		this.onDocumentClick = this.onDocumentClick.bind(this);

		this.trigger.addEventListener("click", this.onTriggerClick);
		this.addEventListener("keydown", this.onKeyDown);
		this.addEventListener("pointerenter", this.onPointerEnter);
		this.addEventListener("pointerleave", this.onPointerLeave);
		this.addEventListener("focusin", this.onFocusIn);
		this.addEventListener("focusout", this.onFocusOut);
		document.addEventListener("click", this.onDocumentClick);
	}

	disconnectedCallback() {
		this.clearCloseTimeout();
		this.trigger?.removeEventListener("click", this.onTriggerClick);
		this.removeEventListener("keydown", this.onKeyDown);
		this.removeEventListener("pointerenter", this.onPointerEnter);
		this.removeEventListener("pointerleave", this.onPointerLeave);
		this.removeEventListener("focusin", this.onFocusIn);
		this.removeEventListener("focusout", this.onFocusOut);
		document.removeEventListener("click", this.onDocumentClick);
	}

	get isCoarsePointer() {
		return window.matchMedia("(hover: none)").matches;
	}

	onTriggerClick(event) {
		if (!this.isCoarsePointer) return;

		event.preventDefault();
		this.toggle();
	}

	onKeyDown(event) {
		if (event.key !== "Escape") return;

		this.close();
		this.trigger?.focus();
	}

	onPointerEnter() {
		if (this.isCoarsePointer) return;
		this.clearCloseTimeout();
		this.open();
	}

	onPointerLeave(event) {
		if (this.isCoarsePointer) return;
		if (this.contains(event.relatedTarget) || this.contains(document.activeElement)) return;
		this.scheduleClose();
	}

	onFocusIn() {
		if (this.isCoarsePointer) return;
		this.open();
	}

	onFocusOut() {
		requestAnimationFrame(() => {
			if (!this.contains(document.activeElement)) this.close();
		});
	}

	onDocumentClick(event) {
		if (this.contains(event.target)) return;
		this.close();
	}

	clearCloseTimeout() {
		if (this.closeTimeout) {
			clearTimeout(this.closeTimeout);
			this.closeTimeout = null;
		}
	}

	scheduleClose() {
		this.clearCloseTimeout();
		this.closeTimeout = setTimeout(() => {
			this.closeTimeout = null;
			if (this.contains(document.activeElement)) return;
			this.close();
		}, this.closeDelayMs);
	}

	open() {
		this.clearCloseTimeout();
		document.querySelectorAll("mega-menu-columns").forEach((menu) => {
			if (menu !== this) menu.close();
		});
		this.syncHoverBridge();
		this.item?.classList.add("is-open");
		this.trigger?.setAttribute("aria-expanded", "true");
		this.panel?.setAttribute("aria-hidden", "false");
	}

	syncHoverBridge() {
		const rect = this.getBoundingClientRect();
		const headerBottomRaw = getComputedStyle(document.documentElement)
			.getPropertyValue("--header-bottom")
			.trim();
		const headerBottom = parseFloat(headerBottomRaw) || rect.bottom;
		const top = Math.max(0, rect.bottom - 4);
		const height = Math.max(headerBottom - top + 12, 12);

		this.style.setProperty("--mega-bridge-top", `${top}px`);
		this.style.setProperty("--mega-bridge-height", `${height}px`);
	}

	close() {
		this.clearCloseTimeout();
		this.item?.classList.remove("is-open");
		this.trigger?.setAttribute("aria-expanded", "false");
		this.panel?.setAttribute("aria-hidden", "true");
	}

	toggle() {
		if (this.item?.classList.contains("is-open")) {
			this.close();
		} else {
			this.open();
		}
	}
}

customElements.define("mega-menu-columns", MegaMenuColumns);
